import React, { useState } from 'react';

export default function ContactForm() {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const email = formData.get('email') as string | null;
    const message = formData.get('wiadomosc') as string | null;

    if (!email?.trim() || !message?.trim()) {
      setSubmitStatus('error');
      return;
    }

    if (message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setSubmitStatus('sending');

    const urlEncodedBody = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        urlEncodedBody.append(key, value);
      }
    });

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d6c05mbv53dtlm7j7t5g',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: urlEncodedBody.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        console.error('Form submission failed:', response.status);
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 5000);
      }
    } catch (error) {
      console.error('Network or unexpected error:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <section
      id="kontakt"
      className="py-24 px-6 bg-gradient-to-b from-white to-stone-50"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-6">
            Kontakt B2B
          </h2>
          <div className="w-24 h-1 bg-stone-300 mx-auto mb-8"></div>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto leading-relaxed">
            Skontaktuj się z naszymi przedstawicielami w Polsce, aby uzyskać
            szczegółowe informacje, materiały B2B i wsparcie w organizacji
            pobytów dla swoich klientów.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Florencio González */}
          <div className="bg-stone-800 text-white rounded-xl p-8">
            <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-full mb-6">
              <i className="ri-user-line text-3xl"></i>
            </div>
            <h3 className="text-2xl font-medium mb-2">Florencio González</h3>
            <p className="text-stone-300 mb-6">Przedstawiciel w Polsce</p>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full shrink-0">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <div>
                  <p className="font-medium mb-1">Telefon</p>
                  <a
                    href="tel:+48728958974"
                    className="text-stone-300 text-sm hover:text-white transition-colors"
                  >
                    +48 728 958 974
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <p className="font-medium mb-1">Email</p>
                  <a
                    href="mailto:f.gonzalez@hiszpaniadeluxe.com"
                    className="text-stone-300 text-sm hover:text-white transition-colors break-all"
                  >
                    f.gonzalez@hiszpaniadeluxe.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Oscar Durán Sanclemente */}
          <div className="bg-stone-800 text-white rounded-xl p-8">
            <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-full mb-6">
              <i className="ri-user-line text-3xl"></i>
            </div>
            <h3 className="text-2xl font-medium mb-2">Oscar Durán Sanclemente</h3>
            <p className="text-stone-300 mb-6">Przedstawiciel w Polsce</p>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full shrink-0">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <div>
                  <p className="font-medium mb-1">Telefon</p>
                  <a
                    href="tel:+48782166928"
                    className="text-stone-300 text-sm hover:text-white transition-colors"
                  >
                    +48 782 166 928
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <p className="font-medium mb-1">Email</p>
                  <a
                    href="mailto:o.duran@hiszpaniadeluxe.com"
                    className="text-stone-300 text-sm hover:text-white transition-colors break-all"
                  >
                    o.duran@hiszpaniadeluxe.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Shared info row */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-stone-50 rounded-xl p-8 border border-stone-200">
            <h4 className="text-xl font-medium text-stone-800 mb-4">
              Strony internetowe
            </h4>
            <div className="space-y-2">
              <a
                href="https://www.jardin-tecina.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-600 text-sm hover:text-stone-900 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-global-line text-lg"></i> www.jardin-tecina.com
              </a>
              <a
                href="https://www.tecinagolf.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-600 text-sm hover:text-stone-900 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-global-line text-lg"></i> www.tecinagolf.com
              </a>
              <a
                href="https://www.hiszpaniadeluxe.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-600 text-sm hover:text-stone-900 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-global-line text-lg"></i> www.hiszpaniadeluxe.com
              </a>
            </div>
          </div>

          <div className="bg-stone-50 rounded-xl p-8 border border-stone-200">
            <h4 className="text-xl font-medium text-stone-800 mb-4">
              Social Media
            </h4>
            <div className="space-y-2">
              <a
                href="https://www.facebook.com/HiszpaniaDeluxe"
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-600 text-sm hover:text-stone-900 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-facebook-fill text-lg"></i> @HiszpaniaDeluxe
              </a>
              <a
                href="https://www.linkedin.com/company/hiszpaniadeluxe"
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-600 text-sm hover:text-stone-900 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-linkedin-fill text-lg"></i> @HiszpaniaDeluxe
              </a>
            </div>
          </div>
        </div>

        {/* W czym możemy pomóc */}
        <div className="bg-stone-50 rounded-xl p-8 border border-stone-200 mb-12">
          <h4 className="text-xl font-medium text-stone-800 mb-6">
            W czym możemy pomóc?
          </h4>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 text-stone-600">
              <i className="ri-check-line text-emerald-600 text-xl shrink-0 mt-0.5"></i>
              <span>
                Materiały promocyjne i zdjęcia wysokiej rozdzielczości
              </span>
            </div>
            <div className="flex items-start gap-3 text-stone-600">
              <i className="ri-check-line text-emerald-600 text-xl shrink-0 mt-0.5"></i>
              <span>Oferty specjalne i ceny B2B dla agencji</span>
            </div>
            <div className="flex items-start gap-3 text-stone-600">
              <i className="ri-check-line text-emerald-600 text-xl shrink-0 mt-0.5"></i>
              <span>Organizacja wizyt studyjnych dla agentów</span>
            </div>
            <div className="flex items-start gap-3 text-stone-600">
              <i className="ri-check-line text-emerald-600 text-xl shrink-0 mt-0.5"></i>
              <span>Wsparcie logistyczne: transfery, promy, pakiety</span>
            </div>
            <div className="flex items-start gap-3 text-stone-600">
              <i className="ri-check-line text-emerald-600 text-xl shrink-0 mt-0.5"></i>
              <span>Szybkie wyceny i rezerwacje grupowe</span>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="mt-12 max-w-xl mx-auto">
          <form id="contact-form-hiszpania" data-readdy-form onSubmit={handleSubmit} className="grid gap-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-stone-700 mb-1"
              >
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                required
                className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label
                htmlFor="wiadomosc"
                className="block text-sm font-medium text-stone-700 mb-1"
              >
                Wiadomość
              </label>
              <textarea
                name="wiadomosc"
                id="wiadomosc"
                rows={5}
                maxLength={500}
                required
                className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              ></textarea>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-md text-sm">
                Dziękujemy! Twoja wiadomość została wysłana. Skontaktujemy się wkrótce.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                Wystąpił błąd. Proszę wypełnić wszystkie pola i spróbować ponownie.
              </div>
            )}

            <button
              type="submit"
              disabled={submitStatus === 'sending'}
              className="self-start bg-emerald-600 text-white px-6 py-2 rounded-md hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {submitStatus === 'sending' ? 'Wysyłanie...' : 'Wyślij'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
